import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Megaphone, Palette, BarChart3, CreditCard, UserCog, Plus, LogOut, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/advertiser", label: "Dashboard", Icon: LayoutDashboard, exact: true, testid: "adv-nav-dashboard" },
  { to: "/advertiser/campaigns", label: "Campaigns", Icon: Megaphone, testid: "adv-nav-campaigns" },
  { to: "/advertiser/creative-studio", label: "Creative Studio", Icon: Palette, testid: "adv-nav-creative" },
  { to: "/advertiser/analytics", label: "Analytics", Icon: BarChart3, testid: "adv-nav-analytics" },
  { to: "/advertiser/billing", label: "Billing", Icon: CreditCard, testid: "adv-nav-billing" },
  { to: "/advertiser/account", label: "Account", Icon: UserCog, testid: "adv-nav-account" },
];

export const AdvShell = ({ children }) => {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { logout } = useAuth();
  const [me, setMe] = useState(null);

  useEffect(() => { api.get("/adv/me").then((r) => setMe(r.data)).catch(() => {}); }, [pathname]);

  const isActive = (item) => item.exact ? pathname === item.to : pathname.startsWith(item.to);
  const doLogout = async () => { await logout(); nav("/advertiser/login"); };

  return (
    <div className="min-h-screen bg-race-bg text-race-txt flex flex-col lg:flex-row" data-testid="adv-shell">
      {/* Sidebar */}
      <aside className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-race-line bg-[#0c0c0f] flex lg:flex-col">
        <div className="p-5 lg:p-6">
          <Link to="/advertiser" className="flex items-baseline gap-2">
            <span className="font-heading text-2xl tracking-tight text-race-txt">TEXT RACING</span>
          </Link>
          <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-race-volt font-semibold">Advertiser</div>
        </div>
        <nav className="flex lg:flex-col gap-1 px-3 pb-4 overflow-x-auto">
          {NAV.map((item) => {
            const active = isActive(item);
            return (
              <Link key={item.to} to={item.to} data-testid={item.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm whitespace-nowrap transition-colors ${
                  active ? "bg-race-surface text-race-txt font-semibold border border-race-line" : "text-race-sub hover:text-race-txt hover:bg-race-surface/60"}`}>
                <item.Icon size={17} className={active ? "text-race-volt" : ""} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="mt-auto p-4 hidden lg:block border-t border-race-line">
          <div className="text-xs text-race-muted truncate mb-2">{me?.business_name || me?.email}</div>
          <button onClick={doLogout} data-testid="adv-logout" className="flex items-center gap-2 text-sm text-race-sub hover:text-race-red transition-colors">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-race-bg/90 backdrop-blur border-b border-race-line px-5 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="text-xs text-race-muted hidden sm:block">Advertising Console</div>
          <div className="flex items-center gap-3 ml-auto">
            <button onClick={doLogout} className="lg:hidden text-race-sub" data-testid="adv-logout-mobile"><LogOut size={18} /></button>
            <Link to="/advertiser/campaigns/new" data-testid="adv-create-cta"
              className="inline-flex items-center gap-2 bg-race-volt text-race-bg font-semibold text-sm px-4 py-2 rounded-md hover:opacity-90 transition-opacity">
              <Plus size={16} /> Create Campaign
            </Link>
          </div>
        </header>
        {me?.ad_account_status === "past_due" && (
          <div className="mx-5 lg:mx-10 mt-5 flex items-start gap-3 rounded-md border border-race-red/50 bg-race-red/10 px-4 py-3" data-testid="adv-pastdue-banner">
            <AlertTriangle size={18} className="text-race-red mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold text-race-txt">Payment issue — campaigns paused</div>
              <div className="text-race-sub">We couldn't process your latest advertising payment. <Link to="/advertiser/billing" className="text-race-volt hover:underline">Update payment method or retry →</Link></div>
            </div>
          </div>
        )}
        <div className="px-5 lg:px-10 py-6 lg:py-8 max-w-6xl">{children}</div>
      </main>
    </div>
  );
};

export default AdvShell;
